import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useLocation } from "react-router-dom";
import { useActiveAccount } from "applesauce-react/hooks";
import { useResolvedRepository } from "@/hooks/useResolvedRepository";
import RepoIssuesPage from "./RepoIssuesPage";
import RepoPRsPage from "./RepoPRsPage";
import RepoCodePage from "./RepoCodePage";
import RepoAboutPage from "./RepoAboutPage";
import RepoSettingsPage from "./RepoSettingsPage";
import RepoCommitsPage from "./RepoCommitsPage";
import RepoCommitPage from "./RepoCommitPage";
import RepoBranchesPage from "./RepoBranchesPage";
import RepoTagsPage from "./RepoTagsPage";
import RepoActionsPage from "./RepoActionsPage";
import IssuePage from "@/pages/IssuePage";
import PRPage from "@/pages/PRPage";
import { useIssues } from "@/hooks/useIssues";
import { usePRs } from "@/hooks/usePRs";
import { useRepoHasCI } from "@/hooks/useCI";
import { usePrefetchNip05 } from "@/hooks/usePrefetchNip05";
import { useDnsIdentity } from "@/hooks/useDnsIdentity";
import { useRepositoryState } from "@/hooks/useRepositoryState";
import { use$ } from "@/hooks/use$";
import { useProfile } from "@/hooks/useProfile";
import { useLoadProfile } from "@/hooks/useLoadProfile";
import { useUserPath } from "@/hooks/useUserPath";
import { UserAvatar } from "@/components/UserAvatar";
import { EventSearchStatus } from "@/components/EventSearchStatus";
import { nip34SupplementalRelayLoader } from "@/services/nostr";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { nip19 } from "nostr-tools";
import {
  ArrowLeft,
  CircleDot,
  GitPullRequest,
  AlertCircle,
  Loader2,
  Code2,
  Info,
  MoreHorizontal,
  Settings,
  Workflow,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RepoContext, type RepoContextValue } from "./RepoContext";
import { type RepoQueryOptions } from "@/lib/nip34";
import { relayCurationMode } from "@/services/settings";
import { cn } from "@/lib/utils";
import { StarButton } from "@/components/StarButton";
import { FollowRepoButton } from "@/components/FollowRepoButton";
import { RepoZapButton } from "@/components/RepoZapButton";
import {
  parseRepoRoute,
  decodeEventIdentifier,
  isEventIdentifier,
  repoToPath,
} from "@/lib/routeUtils";
import {
  GitCommitLinkContext,
  type GitCommitLinkContextValue,
} from "@/components/CommitLinkContext";
import {
  PrivateRepositoryContext,
  RepoRelaysContext,
} from "@/contexts/RepoRelaysContext";
import { relayGroupUrls$ } from "@/models/RepositoryRelayGroup";
import { createGitHttpAuthorizationProvider } from "@/lib/git-http-auth";
import { EMPTY } from "rxjs";
import { catchError } from "rxjs/operators";
import { normalizeUrl } from "@/lib/url";
import { verifyPrivateGraspService } from "@/lib/private-grasp";
import {
  markVerifiedPrivateGitCloneUrl,
  privateGitRelayUrls$,
  verifiedPrivateGitCloneUrls$,
} from "@/services/privateRepoAccess";
// ---------------------------------------------------------------------------
// RepoLayout
// ---------------------------------------------------------------------------

export default function RepoLayout() {
  // The splat param (*) captures everything after the leading /
  const { "*": splat } = useParams<{ "*": string }>();
  const location = useLocation();

  // Use location.pathname rather than the decoded wildcard param when parsing
  // the route. This preserves a literal percent sequence in a d-tag (for
  // example `%2F` is emitted as `%252F`) and guarantees identifiers are
  // decoded exactly once by parseRepoRoute.
  const parsed = useMemo(
    () => parseRepoRoute(location.pathname.slice(1)),
    [location.pathname],
  );

  // If the path doesn't parse as a repo route at all, show not-found immediately
  if (!parsed) {
    return <RouteNotFound splat={splat ?? ""} />;
  }

  if (parsed.type === "npub") {
    return (
      <RepoLayoutResolved
        pubkey={parsed.pubkey}
        repoId={parsed.repoId}
        relayHints={parsed.relayHints}
        location={location}
        splat={splat ?? ""}
      />
    );
  }

  // nip05 — needs async resolution
  return (
    <RepoLayoutNip05
      nip05={parsed.nip05}
      repoId={parsed.repoId}
      relayHints={parsed.relayHints}
      location={location}
      splat={splat ?? ""}
    />
  );
}

// ---------------------------------------------------------------------------
// NIP-05 resolver wrapper
// ---------------------------------------------------------------------------

function RepoLayoutNip05({
  nip05,
  repoId,
  relayHints,
  location,
  splat,
}: {
  nip05: string;
  repoId: string;
  relayHints: string[];
  location: ReturnType<typeof useLocation>;
  splat: string;
}) {
  const identity = useDnsIdentity(nip05);

  if (identity.status === "loading") {
    return <Nip05LoadingState nip05={nip05} />;
  }

  if (identity.status === "not-found") {
    return <Nip05NotFoundError nip05={nip05} />;
  }

  if (identity.status === "error") {
    return <Nip05ResolveError nip05={nip05} reason={identity.reason} />;
  }

  return (
    <RepoLayoutResolved
      pubkey={identity.pubkey}
      repoId={repoId}
      nip05Relays={identity.relays}
      relayHints={relayHints}
      location={location}
      splat={splat}
      nip05={nip05}
    />
  );
}

// ---------------------------------------------------------------------------
// Core layout (pubkey already known)
// ---------------------------------------------------------------------------

function RepoLayoutResolved({
  pubkey,
  repoId,
  nip05Relays,
  relayHints,
  location,
  splat,
  nip05,
}: {
  pubkey: string;
  repoId: string;
  /** Relay hints from NIP-05 identity resolution (shown as their own group). */
  nip05Relays?: string[];
  relayHints: string[];
  location: ReturnType<typeof useLocation>;
  splat: string;
  nip05?: string;
}) {
  const { resolved, repoSearch } = useResolvedRepository(
    pubkey,
    repoId,
    relayHints,
    nip05Relays,
  );
  const repo = resolved?.repo;

  // Delay showing the repo search status page so the skeleton shows first.
  // Timer starts on mount (keyed to pubkey+repoId) and is never reset by
  // transient relay group changes mid-search.
  const [repoSearchDelayElapsed, setRepoSearchDelayElapsed] = useState(false);
  useEffect(() => {
    setRepoSearchDelayElapsed(false);
    const timer = setTimeout(() => setRepoSearchDelayElapsed(true), 1500);
    return () => clearTimeout(timer);
  }, [pubkey, repoId]);

  // Prefetch NIP-05 identities for all maintainers so useRepoPath can resolve
  // them synchronously from the IDB cache on subsequent visits.
  usePrefetchNip05(repo?.maintainerSet ?? []);
  const repoRelayGroup = resolved?.repoRelayGroup;
  const extraRelaysForMaintainerMailboxCoverage =
    resolved?.extraRelaysForMaintainerMailboxCoverage;

  // Reactive relay URL list for RepoRelaysContext — updates as outbox relays
  // are discovered so any ZapModal rendered under this layout uses the current
  // full set when building the zap request's relays tag.
  const repoRelayUrls =
    use$(() => relayGroupUrls$(repoRelayGroup), [repoRelayGroup]) ?? [];

  // Respect the user's relay curation preference.
  const curationMode = use$(relayCurationMode);

  // In outbox mode, also subscribe to the extra maintainer mailbox relays so
  // issues and PRs published only to those relays are discovered. Uses
  // nip34SupplementalRelayLoader which — unlike a plain subscription — also
  // calls nip34ListLoader for each newly found item, ensuring status events
  // (1630-1633) and other essentials on author/maintainer outbox relays are
  // fetched, not just the root events.
  const coordKey = repo?.allCoordinates?.join(",") ?? "";
  use$(() => {
    if (
      curationMode !== "outbox" ||
      repo?.isPrivate ||
      !extraRelaysForMaintainerMailboxCoverage ||
      !repo?.allCoordinates?.length
    )
      return undefined;
    return nip34SupplementalRelayLoader(
      repo.allCoordinates,
      extraRelaysForMaintainerMailboxCoverage,
    ).pipe(catchError(() => EMPTY));
  }, [
    curationMode,
    extraRelaysForMaintainerMailboxCoverage,
    coordKey,
    repo?.isPrivate,
  ]);

  const relayHintsKey = relayHints.join(",");
  const repoMaintainersKey = repo?.maintainerSet?.join(",") ?? "";
  const queryOptions: RepoQueryOptions = useMemo(
    () => ({
      relayHints: repo?.isPrivate ? [] : relayHints,
      useItemAuthorRelays: false,
      maintainerPubkeys: repo?.maintainerSet ?? [],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [relayHintsKey, repoMaintainersKey, repo?.isPrivate],
  );

  const issues = useIssues(repo?.allCoordinates, repoRelayGroup, queryOptions);
  const prs = usePRs(repo?.allCoordinates, repoRelayGroup, queryOptions);

  // Whether the repo has any CI events (ngit-ci kinds 9841/9842) — drives
  // visibility of the Actions tab. Cheap limit-1 probe by #a across all
  // maintainer coordinates.
  const hasCI = useRepoHasCI(repo?.allCoordinates, repoRelayGroup);

  const [repoState, repoRelayEose, relayStateMap] = useRepositoryState(
    repo?.dTag,
    repo?.maintainerSet,
    repoRelayGroup,
  );

  // Count open issues for the tab badge
  const openIssueCount = useMemo(() => {
    if (!issues) return undefined;
    return issues.filter((i) => i.status === "open").length;
  }, [issues]);

  // Count open PRs for the tab badge
  const openPRCount = useMemo(() => {
    if (!prs) return undefined;
    return prs.filter((p) => p.status === "open").length;
  }, [prs]);

  // Determine if the logged-in user is a confirmed maintainer of this repo.
  const account = useActiveAccount();
  const gitAuthorizationProvider = useMemo(
    () =>
      resolved?.repo.isPrivate && account
        ? createGitHttpAuthorizationProvider(account.pubkey, account.signer)
        : undefined,
    [resolved?.repo.isPrivate, account],
  );
  const trustedPrivateRelays = use$(privateGitRelayUrls$) ?? [];
  const verifiedPrivateCloneUrls = use$(verifiedPrivateGitCloneUrls$) ?? [];
  const trustedPrivateRelayKey = trustedPrivateRelays.join(",");
  const verifiedPrivateCloneKey = verifiedPrivateCloneUrls.join(",");

  useEffect(() => {
    if (
      !repo?.isPrivate ||
      !account ||
      !gitAuthorizationProvider ||
      trustedPrivateRelays.length === 0
    ) {
      return;
    }

    const trusted = new Set(trustedPrivateRelays.map(normalizeUrl));
    const verified = new Set(verifiedPrivateCloneUrls.map(normalizeUrl));
    for (const cloneUrl of repo.graspCloneUrls) {
      if (verified.has(normalizeUrl(cloneUrl))) continue;
      let cloneHost: string;
      try {
        cloneHost = new URL(cloneUrl).host;
      } catch {
        continue;
      }
      const relayUrl = repo.relays.find((candidate) => {
        try {
          return (
            trusted.has(normalizeUrl(candidate)) &&
            new URL(candidate).host === cloneHost
          );
        } catch {
          return false;
        }
      });
      if (!relayUrl) continue;

      void verifyPrivateGraspService(
        cloneUrl,
        relayUrl,
        gitAuthorizationProvider,
        account.signer,
      )
        .then(() => markVerifiedPrivateGitCloneUrl(cloneUrl, relayUrl))
        .catch((err) =>
          console.warn(
            `[private-repos] Quarantined unverified clone endpoint ${cloneUrl}:`,
            err,
          ),
        );
    }
  }, [
    repo?.isPrivate,
    repo?.graspCloneUrls.join(","),
    repo?.relays.join(","),
    account,
    gitAuthorizationProvider,
    trustedPrivateRelayKey,
    verifiedPrivateCloneKey,
  ]);
  const isMaintainer =
    account?.pubkey && repo
      ? repo.maintainerSet.includes(account.pubkey)
      : false;

  // Build an encoded base path for intra-repository links. `splat` is decoded
  // by React Router, including `%2F` inside a repository identifier; using it
  // directly would turn an identifier such as `lightningdevkit/rust-lightning`
  // into multiple path segments when linking to `/prs`, `/issues`, etc.
  const basePath = useMemo(() => {
    return repoToPath(pubkey, repoId, relayHints, nip05);
  }, [pubkey, repoId, relayHints, nip05]);

  const isCodeTab =
    location.pathname.startsWith(`${basePath}/tree`) ||
    location.pathname === basePath ||
    location.pathname === `${basePath}/`;
  const isIssuesTab = location.pathname.startsWith(`${basePath}/issues`);
  const isPRsTab = location.pathname.startsWith(`${basePath}/prs`);
  const isActionsTab = location.pathname.startsWith(`${basePath}/actions`);
  const isAboutTab = location.pathname.startsWith(`${basePath}/about`);
  const isSettingsTab = location.pathname.startsWith(`${basePath}/settings`);
  // Determine which sub-page to render from the splat segments.
  const {
    subPage,
    issueId,
    prId,
    treeRefAndPath,
    commitId,
    commitsRef,
    prCommitId,
  } = useMemo((): {
    subPage:
      | "code"
      | "issues"
      | "issue"
      | "prs"
      | "pr"
      | "pr-commit"
      | "commits"
      | "commit"
      | "branches"
      | "tags"
      | "actions"
      | "about"
      | "edit"
      | "settings";
    issueId?: string;
    prId?: string;
    /** Everything after /tree/ — ref resolution happens inside useGitExplorer */
    treeRefAndPath?: string;
    commitId?: string;
    commitsRef?: string;
    prCommitId?: string;
  } => {
    const segments = splat.split("/").filter(Boolean);

    // Find the index of the first known sub-path keyword
    const treeIdx = segments.indexOf("tree");
    if (treeIdx !== -1) {
      // Pass everything after "tree" as a single string; useGitExplorer will
      // resolve the ref via longest-prefix matching against known git refs.
      const refAndPath = segments.slice(treeIdx + 1).join("/");
      return { subPage: "code", treeRefAndPath: refAndPath || undefined };
    }

    const prsIdx = segments.indexOf("prs");
    if (prsIdx !== -1) {
      if (segments.length > prsIdx + 1) {
        const rawSegment = segments[prsIdx + 1];
        // Accept both raw hex IDs (legacy) and nevent1/note1 identifiers
        const prId = isEventIdentifier(rawSegment)
          ? decodeEventIdentifier(rawSegment)
          : rawSegment;

        // prs/<id>/commit/<hash|nevent1|note1> — commit detail scoped to a PR
        const prCommitIdx = segments.indexOf("commit", prsIdx + 2);
        if (prCommitIdx !== -1) {
          const rawCommitSeg = segments[prCommitIdx + 1];
          // Accept nevent1/note1 (decode to event ID) or raw hex (pass through).
          // Raw hex may be either a git commit hash or a Nostr event ID —
          // PRPage's patchMatch handles both.
          const prCommitId = isEventIdentifier(rawCommitSeg)
            ? decodeEventIdentifier(rawCommitSeg)
            : rawCommitSeg;
          return {
            subPage: "pr-commit",
            prId,
            prCommitId,
          };
        }

        // prs/<id>/commits — commits list scoped to a PR (renders PRPage
        // with the commits tab pre-selected via context)
        const prCommitsIdx = segments.indexOf("commits", prsIdx + 2);
        if (prCommitsIdx !== -1) {
          return { subPage: "pr", prId };
        }

        return { subPage: "pr", prId };
      }
      return { subPage: "prs" };
    }

    const commitIdx = segments.indexOf("commit");
    if (commitIdx !== -1) {
      return { subPage: "commit", commitId: segments[commitIdx + 1] };
    }

    const commitsIdx = segments.indexOf("commits");
    if (commitsIdx !== -1) {
      return {
        subPage: "commits",
        commitsRef: segments.slice(commitsIdx + 1).join("/") || undefined,
      };
    }

    const branchesIdx = segments.indexOf("branches");
    if (branchesIdx !== -1) {
      return { subPage: "branches" };
    }

    const tagsIdx = segments.indexOf("tags");
    if (tagsIdx !== -1) {
      return { subPage: "tags" };
    }

    const actionsIdx = segments.indexOf("actions");
    if (actionsIdx !== -1) {
      return { subPage: "actions" };
    }

    const issuesIdx = segments.indexOf("issues");
    if (issuesIdx !== -1) {
      if (segments.length > issuesIdx + 1) {
        const rawSegment = segments[issuesIdx + 1];
        // Accept both raw hex IDs (legacy) and nevent1/note1 identifiers
        const issueId = isEventIdentifier(rawSegment)
          ? decodeEventIdentifier(rawSegment)
          : rawSegment;
        return { subPage: "issue", issueId };
      }
      return { subPage: "issues" };
    }

    const editIdx = segments.indexOf("edit");
    if (editIdx !== -1) {
      return { subPage: "edit" };
    }

    const aboutIdx = segments.indexOf("about");
    if (aboutIdx !== -1) {
      return { subPage: "about" };
    }

    const settingsIdx = segments.indexOf("settings");
    if (settingsIdx !== -1) {
      return { subPage: "settings" };
    }

    return { subPage: "code" };
  }, [splat]);

  const cloneUrls = repo?.isPrivate
    ? repo.cloneUrls.filter((url) =>
        verifiedPrivateCloneUrls.includes(normalizeUrl(url)),
      )
    : (repo?.cloneUrls ?? []);

  // The PR base path: basePath + /prs/<prId> — used for PR sub-route links.
  const prBasePath = useMemo(() => {
    if (!prId) return undefined;
    const segments = splat.split("/").filter(Boolean);
    const prsIdx = segments.indexOf("prs");
    const prIdSegment = segments[prsIdx + 1];
    return prsIdx === -1 || !prIdSegment
      ? undefined
      : `${basePath}/prs/${prIdSegment}`;
  }, [basePath, splat, prId]);

  const ctxValue: RepoContextValue | null =
    pubkey && repoId && resolved
      ? {
          pubkey,
          repoId,
          resolved,
          repoSearch,
          issues,
          prs,
          queryOptions,
          nip05,
          issueId,
          prId,
          cloneUrls,
          repoState,
          repoRelayEose,
          relayStateMap,
          treeRefAndPath,
          commitId,
          commitsRef,
          prCommitId,
          prBasePath,
          basePath,
        }
      : null;

  // Build the git commit link context — provides cloneUrls + basePath to
  // CommentContent / MarkdownContent for linkifying commit hash mentions.
  const cloneUrlsKey = cloneUrls.join(",");
  const gitCommitLinkCtxValue: GitCommitLinkContextValue = useMemo(
    () => ({
      cloneUrls:
        resolved?.repo.isPrivate && !gitAuthorizationProvider ? [] : cloneUrls,
      basePath,
      authorizationProvider: gitAuthorizationProvider,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cloneUrlsKey,
      basePath,
      resolved?.repo.isPrivate,
      gitAuthorizationProvider,
    ],
  );

  return (
    <PrivateRepositoryContext.Provider value={repo?.isPrivate ?? false}>
      <RepoRelaysContext.Provider value={repoRelayUrls}>
        <div className="min-h-full">
          {/* Repo header */}
          <div className="relative isolate border-b border-border/40">
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-pink-500/5 via-transparent to-pink-500/5" />

            <div className="container max-w-screen-xl px-4 md:px-8 pt-6 pb-0">
              {repo ? (
                <div className="flex items-center justify-between gap-3 mb-4 min-w-0 overflow-hidden">
                  <RepoBreadcrumb
                    pubkey={pubkey}
                    repoName={repo.name}
                    basePath={basePath}
                    nip05={nip05}
                  />
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <RepoZapButton
                      targetAnnouncement={repo.announcements.find(
                        (a) => a.pubkey === repo.selectedMaintainer,
                      )}
                      repoCoords={repo.allCoordinates}
                    />
                    <FollowRepoButton allCoords={repo.allCoordinates} />
                    <StarButton
                      targetAnnouncement={repo.announcements.find(
                        (a) => a.pubkey === repo.selectedMaintainer,
                      )}
                      allAnnouncements={repo.announcements}
                      repoCoords={repo.allCoordinates}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 mb-4">
                  <Skeleton className="h-5 w-24" />
                  <span className="text-muted-foreground">/</span>
                  <Skeleton className="h-5 w-32" />
                </div>
              )}

              {/* Tab navigation */}
              <nav className="flex gap-1 -mb-px">
                {/* Primary tabs — always visible */}
                <TabLink
                  to={basePath}
                  active={isCodeTab}
                  icon={<Code2 className="h-4 w-4" />}
                  label="Code"
                />
                <TabLink
                  to={`${basePath}/issues`}
                  active={isIssuesTab}
                  icon={<CircleDot className="h-4 w-4" />}
                  label="Issues"
                  count={openIssueCount}
                />
                <TabLink
                  to={`${basePath}/prs`}
                  active={isPRsTab}
                  icon={<GitPullRequest className="h-4 w-4" />}
                  label="PRs"
                  count={openPRCount}
                />

                {/* Secondary tabs — visible on md+ screens */}
                <div className="hidden md:flex gap-1">
                  {(hasCI || isActionsTab) && (
                    <TabLink
                      to={`${basePath}/actions`}
                      active={isActionsTab}
                      icon={<Workflow className="h-4 w-4" />}
                      label="Actions"
                    />
                  )}
                  <TabLink
                    to={`${basePath}/about`}
                    active={isAboutTab}
                    icon={<Info className="h-4 w-4" />}
                    label="About"
                  />
                  {isMaintainer && (
                    <TabLink
                      to={`${basePath}/settings`}
                      active={isSettingsTab}
                      icon={<Settings className="h-4 w-4" />}
                      label="Settings"
                    />
                  )}
                </div>

                {/* "More" dropdown — mobile only */}
                <div className="md:hidden flex items-end pb-px">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors",
                          isAboutTab || isSettingsTab || isActionsTab
                            ? "border-pink-500 text-foreground"
                            : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
                        )}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">More</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {(hasCI || isActionsTab) && (
                        <DropdownMenuItem asChild>
                          <Link
                            to={`${basePath}/actions`}
                            className="flex items-center gap-2"
                          >
                            <Workflow className="h-4 w-4" />
                            Actions
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <Link
                          to={`${basePath}/about`}
                          className="flex items-center gap-2"
                        >
                          <Info className="h-4 w-4" />
                          About
                        </Link>
                      </DropdownMenuItem>
                      {isMaintainer && (
                        <DropdownMenuItem asChild>
                          <Link
                            to={`${basePath}/settings`}
                            className="flex items-center gap-2"
                          >
                            <Settings className="h-4 w-4" />
                            Settings
                          </Link>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </nav>
            </div>
          </div>

          {/* Page content */}
          {ctxValue ? (
            <GitCommitLinkContext.Provider value={gitCommitLinkCtxValue}>
              <RepoContext.Provider value={ctxValue}>
                {subPage === "code" ? (
                  <RepoCodePage />
                ) : subPage === "commits" ? (
                  <RepoCommitsPage />
                ) : subPage === "commit" ? (
                  <RepoCommitPage />
                ) : subPage === "branches" ? (
                  <RepoBranchesPage />
                ) : subPage === "tags" ? (
                  <RepoTagsPage />
                ) : subPage === "actions" ? (
                  <RepoActionsPage />
                ) : subPage === "issue" ? (
                  <IssuePage />
                ) : subPage === "issues" ? (
                  <RepoIssuesPage />
                ) : subPage === "pr" ? (
                  <PRPage />
                ) : subPage === "pr-commit" ? (
                  <PRPage />
                ) : subPage === "prs" ? (
                  <RepoPRsPage />
                ) : subPage === "about" ? (
                  <RepoAboutPage />
                ) : subPage === "edit" ? (
                  <Navigate to={`${basePath}/settings`} replace />
                ) : subPage === "settings" ? (
                  <RepoSettingsPage />
                ) : null}
              </RepoContext.Provider>
            </GitCommitLinkContext.Provider>
          ) : repoSearch &&
            (repoSearch.concludedNotFound ||
              repoSearch.deleted ||
              repoSearch.vanished ||
              (Object.keys(repoSearch.relayStatuses).length > 0 &&
                repoSearchDelayElapsed)) &&
            !repoSearch.found ? (
            <EventSearchStatus
              search={repoSearch}
              itemLabel="Repository"
              backPath="/"
              backLabel="Back to repositories"
              searchMoreActive={true}
            />
          ) : subPage === "issue" ||
            subPage === "pr" ||
            subPage === "pr-commit" ? (
            /* Show an issue/PR-shaped skeleton while the repo context resolves,
           so the transition to the real page feels seamless. */
            <>
              <div className="border-b border-border/40">
                <div className="container max-w-screen-xl px-4 md:px-8 pt-6 pb-4">
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <Skeleton className="h-6 w-16 rounded-full" />
                      <Skeleton className="h-7 w-96" />
                    </div>
                    <div className="flex gap-3">
                      <Skeleton className="h-6 w-6 rounded-full" />
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </RepoRelaysContext.Provider>
    </PrivateRepositoryContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Error / loading states
// ---------------------------------------------------------------------------

function Nip05LoadingState({ nip05 }: { nip05: string }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(id);
  }, []);
  return (
    <div
      className={`min-h-full flex items-center justify-center transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0"}`}
    >
      <div className="text-center space-y-4 max-w-md px-4">
        <div className="flex justify-center">
          <div className="p-4 rounded-full bg-pink-500/10">
            <Loader2 className="h-8 w-8 text-pink-500 animate-spin" />
          </div>
        </div>
        <h2 className="text-xl font-semibold">Resolving identity</h2>
        <p className="text-muted-foreground text-sm">
          Looking up <span className="font-mono text-foreground">{nip05}</span>…
        </p>
      </div>
    </div>
  );
}

function Nip05NotFoundError({ nip05 }: { nip05: string }) {
  return (
    <div className="min-h-full flex items-center justify-center">
      <div className="text-center space-y-6 max-w-md px-4">
        <div className="flex justify-center">
          <div className="p-4 rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">Identity not found</h2>
          <p className="text-muted-foreground">
            The NIP-05 address{" "}
            <span className="font-mono text-foreground">{nip05}</span> could not
            be found. Make sure the address is correct and the domain's{" "}
            <span className="font-mono text-sm">/.well-known/nostr.json</span>{" "}
            is reachable.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to repositories
          </Link>
        </Button>
      </div>
    </div>
  );
}

function Nip05ResolveError({
  nip05,
  reason,
}: {
  nip05: string;
  reason: "timeout" | "network" | "unknown";
}) {
  const detail =
    reason === "timeout"
      ? "The lookup timed out. The domain may be slow or unreachable."
      : reason === "network"
        ? "A network error occurred. Check your connection and that the domain's /.well-known/nostr.json is accessible."
        : "An unexpected error occurred while looking up the NIP-05 address.";

  return (
    <div className="min-h-full flex items-center justify-center">
      <div className="text-center space-y-6 max-w-md px-4">
        <div className="flex justify-center">
          <div className="p-4 rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">Failed to resolve identity</h2>
          <p className="text-muted-foreground">
            Could not look up{" "}
            <span className="font-mono text-foreground">{nip05}</span>.
          </p>
          <p className="text-sm text-muted-foreground">{detail}</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to repositories
          </Link>
        </Button>
      </div>
    </div>
  );
}

function RouteNotFound({ splat }: { splat: string }) {
  return (
    <div className="min-h-full flex items-center justify-center">
      <div className="text-center space-y-6 max-w-md px-4">
        <div className="flex justify-center">
          <div className="p-4 rounded-full bg-muted">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">Page not found</h2>
          <p className="text-muted-foreground">
            <span className="font-mono text-foreground">/{splat}</span> doesn't
            match a known repository URL format.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to repositories
          </Link>
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Repo breadcrumb: <username> / <repo-name>
// ---------------------------------------------------------------------------

function RepoBreadcrumb({
  pubkey,
  repoName,
  basePath,
  nip05,
}: {
  pubkey: string;
  repoName: string;
  basePath: string;
  nip05?: string;
}) {
  useLoadProfile(pubkey);
  const profile = useProfile(pubkey);
  const userPath = useUserPath(pubkey);
  const npub = nip19.npubEncode(pubkey);
  const nip05Local = nip05?.split("@")[0];
  const nip05Domain = nip05?.split("@")[1];
  const nip05Label = nip05Local === "_" ? nip05Domain : nip05Local;
  const username =
    profile?.displayName ??
    profile?.name ??
    nip05Label ??
    npub.slice(0, 12) + "…";

  return (
    <div className="flex items-center gap-2 min-w-0 overflow-hidden">
      <div className="flex items-center gap-2 text-muted-foreground min-w-0 shrink">
        <UserAvatar
          pubkey={pubkey}
          size="sm"
          className="flex-shrink-0"
          linkToProfile
        />
        <Link
          to={userPath}
          className="hover:text-foreground transition-colors truncate"
        >
          <span className="text-base font-medium truncate">{username}</span>
        </Link>
      </div>
      <span className="text-muted-foreground font-normal flex-shrink-0">/</span>
      <Link
        to={basePath}
        className="text-base font-semibold text-foreground hover:text-pink-500 transition-colors truncate min-w-0 shrink"
      >
        {repoName}
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab link
// ---------------------------------------------------------------------------

function TabLink({
  to,
  active,
  icon,
  label,
  count,
}: {
  to: string;
  active: boolean;
  icon: React.ReactNode;
  label: string;
  count?: number;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
        active
          ? "border-pink-500 text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
      )}
    >
      {icon}
      {label}
      {count !== undefined && count > 0 && (
        <Badge
          variant="secondary"
          className="ml-1 h-5 min-w-[20px] px-1.5 text-[11px] font-medium"
        >
          {count}
        </Badge>
      )}
    </Link>
  );
}
